import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { BookOpen, Loader2, Search, PlayCircle, CheckCircle2 } from 'lucide-react';

const FILTERS = [['all', 'All'], ['in_progress', 'In progress'], ['completed', 'Completed']];
const norm = (item) => {
  const pct = Math.round(item.progress?.overall_progress_pct || item.progress_percentage || 0);
  const status = item.progress?.status === 'completed' || item.status === 'completed' || pct >= 100 ? 'completed' : 'in_progress';
  return { id: item.course_id || item.id, title: item.course_title || item.title, thumb: item.course_thumbnail || item.thumbnail_url, pct, status, due: item.due_date };
};

const CourseRow = ({ c }) => (
  <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`my-courses-item-${c.id}`}>
    <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="w-full sm:w-28 h-20 rounded-lg overflow-hidden bg-slate-100 shrink-0 flex items-center justify-center">
        {c.thumb ? <img src={c.thumb} alt="" className="w-full h-full object-cover" /> : <BookOpen className="h-6 w-6 text-slate-300" />}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-[#2a3e5d] truncate">{c.title}</h3>
        <div className="flex items-center gap-3 mt-2">
          <Progress value={c.pct} className="h-2 flex-1" />
          <span className="text-xs font-medium text-slate-600 w-10 text-right">{c.pct}%</span>
        </div>
        {c.due && c.status !== 'completed' && <p className="text-xs text-amber-700 mt-1.5">Due {new Date(`${c.due}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>}
      </div>
      <Button asChild className={c.status === 'completed' ? 'bg-white border border-green-600 text-green-700 hover:bg-green-50' : 'bg-[#20acf6] hover:bg-[#3bb8f7]'}>
        <Link to={`/learn/${c.id}`} data-testid={`my-courses-open-${c.id}`}>
          {c.status === 'completed' ? <><CheckCircle2 className="h-4 w-4 mr-2" />Review</> : <><PlayCircle className="h-4 w-4 mr-2" />{c.pct > 0 ? 'Continue' : 'Start'}</>}
        </Link>
      </Button>
    </CardContent>
  </Card>
);

export default function MyCoursesPage() {
  const { api } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  useEffect(() => {
    api.get('/player/my-courses').then((r) => setItems((r.data || []).map(norm))).catch(() => setItems([]));
  }, [api]);
  const shown = useMemo(() => (items || []).filter((c) => (filter === 'all' || c.status === filter) && (c.title || '').toLowerCase().includes(q.toLowerCase())), [items, q, filter]);
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="my-courses-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">My courses</h1>
            <p className="text-slate-500 mt-1 text-sm">{items ? `${items.length} course${items.length === 1 ? '' : 's'}` : 'Loading…'}</p>
          </div>
          <Button asChild variant="outline"><Link to="/courses" data-testid="my-courses-browse">Browse more courses</Link></Button>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your courses…" className="pl-10" data-testid="my-courses-search" />
          </div>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {FILTERS.map(([v, l]) => (
              <button key={v} type="button" onClick={() => setFilter(v)} className={`px-3 py-2 text-sm ${filter === v ? 'bg-[#20acf6] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`my-courses-filter-${v}`}>{l}</button>
            ))}
          </div>
        </div>
        {!items ? <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#20acf6]" /></div>
          : shown.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200" data-testid="my-courses-empty">
              <BookOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">{items.length ? 'No courses match your search.' : "You haven't enrolled in any courses yet."}</p>
              {!items.length && <Button asChild className="mt-4 bg-[#20acf6] hover:bg-[#3bb8f7]"><Link to="/courses">Browse courses</Link></Button>}
            </div>
          ) : <div className="space-y-3">{shown.map((c) => <CourseRow key={c.id} c={c} />)}</div>}
      </div>
    </DashboardLayout>
  );
}
